import { HTTPInstanceFastApiWithPrefix } from '@/lib/http/http-instance-fastapi-with-prefix'
import { HTTPInstanceFastApi } from '@/lib/http/http-instance-fastapi'
import type {
  DateRangeParams,
  ComparisonDateRangeParams,
  TopUnitsParams,
  TopUnitsIngredientsMvParams,
  IngredientComparisonParams,
  ReportParams,
  IngredientReportParams,
  FullReportParams,
  IngredientsFullReportParams,
  GeneralFullReportParams,
  SalesTableParams,
  IngredientsSalesTableParams,
  SalesExcelExportParams,
  IngredientsSalesExcelExportParams,
  SalesPdfExportParams,
  InventoryPdfExportParams,
  OpinionAnalysisRequest,
  OpinionAnalysisResult,
  MenuResponse,
  TotalSalesResponseWithProfit,
  TotalSalesResponseCount,
  TotalSalesResponseCountGranulated,
  TotalSalesResponseAverage,
  TotalSalesResponseComparison,
  TotalSalesResponseAverageComparison,
  TotalSalesResponseComparisonCumulative,
  TopUnitsResponse,
  TopDishToppingResponse,
  SalesTableMultiLang,
  TranslationItem
} from './InsightsAPI.model'

class InsightsAPI {
  // ── General ──

  static getTotalSalesValueGeneral = (
    params: DateRangeParams
  ): Promise<TotalSalesResponseWithProfit> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-value-general', {
      params
    }).then(r => r.data)

  static getTotalSalesCountGeneral = (
    params: DateRangeParams
  ): Promise<TotalSalesResponseCount> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-count-general', {
      params
    }).then(r => r.data)

  static getTotalSalesAveragesGeneral = (
    params: DateRangeParams
  ): Promise<TotalSalesResponseAverage> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-value-averages-general', {
      params
    }).then(r => r.data)

  // ── General reports ──

  static getTotalSalesValueGeneralReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-value-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTotalSalesCountGeneralReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-count-general-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTotalSalesAveragesGeneralReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-value-averages-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  // ── Dishes ──

  static getTotalSalesValueDishes = (
    params: ComparisonDateRangeParams
  ): Promise<TotalSalesResponseComparison> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-value-dishes', {
      params
    }).then(r => r.data)

  static getTotalSalesCountDishes = (
    params: ComparisonDateRangeParams
  ): Promise<TotalSalesResponseCountGranulated> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-count-dishes', {
      params
    }).then(r => r.data)

  static getTotalSalesAveragesDishes = (
    params: ComparisonDateRangeParams
  ): Promise<TotalSalesResponseAverageComparison> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-averages-dishes', {
      params
    }).then(r => r.data)

  static getTotalSalesCumulativeValueDishes = (
    params: ComparisonDateRangeParams
  ): Promise<TotalSalesResponseComparisonCumulative> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-cumulative-value-dishes', {
      params
    }).then(r => r.data)

  // ── Dishes reports ──

  static getTotalSalesValueDishesReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-value-dishes-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTotalSalesCountDishesReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-count-dishes-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTotalSalesAveragesDishesReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-averages-dishes-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTotalSalesCumulativeValueDishesReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get(
      '/total-sales-cumulative-value-dishes-report',
      { params, responseType: 'blob' }
    ).then(r => r.data)

  static getDishesFullReport = (params: FullReportParams): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/dishes-full-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  // ── Toppings ──

  static getTotalSalesValueToppings = (
    params: ComparisonDateRangeParams
  ): Promise<TotalSalesResponseComparison> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-value-toppings', {
      params
    }).then(r => r.data)

  static getTotalSalesCountToppings = (
    params: ComparisonDateRangeParams
  ): Promise<TotalSalesResponseCountGranulated> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-count-toppings', {
      params
    }).then(r => r.data)

  static getTotalSalesAveragesToppings = (
    params: ComparisonDateRangeParams
  ): Promise<TotalSalesResponseAverageComparison> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-averages-toppings', {
      params
    }).then(r => r.data)

  static getTotalSalesCumulativeValueToppings = (
    params: ComparisonDateRangeParams
  ): Promise<TotalSalesResponseComparisonCumulative> =>
    HTTPInstanceFastApiWithPrefix.get(
      '/total-sales-cumulative-value-toppings',
      { params }
    ).then(r => r.data)

  // ── Toppings reports ──

  static getTotalSalesValueToppingsReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-value-toppings-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTotalSalesCountToppingsReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-count-toppings-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTotalSalesAveragesToppingsReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-averages-toppings-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTotalSalesCumulativeValueToppingsReport = (
    params: ReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get(
      '/total-sales-cumulative-value-toppings-report',
      { params, responseType: 'blob' }
    ).then(r => r.data)

  static getToppingsFullReport = (params: FullReportParams): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/toppings-full-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  // ── Ingredients ──

  static getTotalSalesValueIngredients = (
    params: IngredientComparisonParams
  ): Promise<TotalSalesResponseComparison> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-value-ingredients', {
      params
    }).then(r => r.data)

  static getTotalSalesCountIngredients = (
    params: IngredientComparisonParams
  ): Promise<TotalSalesResponseCountGranulated> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-count-ingredients', {
      params
    }).then(r => r.data)

  static getTotalSalesAveragesIngredients = (
    params: IngredientComparisonParams
  ): Promise<TotalSalesResponseAverageComparison> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-averages-ingredients', {
      params
    }).then(r => r.data)

  static getTotalSalesCumulativeValueIngredients = (
    params: IngredientComparisonParams
  ): Promise<TotalSalesResponseComparisonCumulative> =>
    HTTPInstanceFastApiWithPrefix.get(
      '/total-sales-cumulative-value-ingredients',
      { params }
    ).then(r => r.data)

  // ── Ingredients reports ──

  static getTotalSalesValueIngredientsReport = (
    params: IngredientReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-value-ingredients-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTotalSalesCountIngredientsReport = (
    params: IngredientReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/total-sales-count-ingredients-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTotalSalesAveragesIngredientsReport = (
    params: IngredientReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get(
      '/total-sales-averages-ingredients-report',
      { params, responseType: 'blob' }
    ).then(r => r.data)

  static getTotalSalesCumulativeValueIngredientsReport = (
    params: IngredientReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get(
      '/total-cumulative-sales-ingredients-report',
      { params, responseType: 'blob' }
    ).then(r => r.data)

  static getIngredientsFullReport = (
    params: IngredientsFullReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/ingredients-full-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  // ── Top by units ──

  static getTopByUnitsDishes = (
    params: TopUnitsParams
  ): Promise<TopUnitsResponse> =>
    HTTPInstanceFastApiWithPrefix.get('/top-by-units-dishes', { params }).then(
      r => r.data
    )

  static getTopByUnitsToppings = (
    params: TopUnitsParams
  ): Promise<TopUnitsResponse> =>
    HTTPInstanceFastApiWithPrefix.get('/top-by-units-toppings', {
      params
    }).then(r => r.data)

  static getTopByUnitsIngredientsMv = (
    params: TopUnitsIngredientsMvParams
  ): Promise<TopUnitsResponse> =>
    HTTPInstanceFastApiWithPrefix.get('/top-by-units-ingredients-mv', {
      params
    }).then(r => r.data)

  static getTopByUnitsDishToppings = (
    params: TopUnitsParams
  ): Promise<TopDishToppingResponse> =>
    HTTPInstanceFastApiWithPrefix.get('/top-by-units-dish-toppings', {
      params
    }).then(r => r.data)

  // ── Top by units reports ──

  static getTopByUnitsDishesReport = (
    params: TopUnitsParams & { language?: 'pl' | 'en'; include_id?: boolean }
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/top-by-units-dishes-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTopByUnitsToppingsReport = (
    params: TopUnitsParams & { language?: 'pl' | 'en'; include_id?: boolean }
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/top-by-units-toppings-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTopByUnitsIngredientsMvReport = (
    params: TopUnitsIngredientsMvParams & { language?: string }
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/top-by-units-ingredients-mv-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getTopByUnitsDishToppingsReport = (
    params: TopUnitsParams & { language?: 'pl' | 'en' }
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/top-by-units-dish-toppings-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  // ── Full reports ──

  static getGeneralFullReport = (
    params: GeneralFullReportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/general-full-report', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  // ── Sales tables ──

  static getMenuItemsSalesTable = (
    params: SalesTableParams
  ): Promise<SalesTableMultiLang> =>
    HTTPInstanceFastApiWithPrefix.get('/menu-items-sales-table-by-language', {
      params
    }).then(r => r.data)

  static getIngredientsSalesTable = (
    params: IngredientsSalesTableParams
  ): Promise<SalesTableMultiLang> =>
    HTTPInstanceFastApiWithPrefix.get('/ingredients-sales-table-by-language', {
      params
    }).then(r => r.data)

  // ── Sales exports ──

  static getMenuItemsSalesExcel = (
    params: SalesExcelExportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/sales/menu-items-by-language/excel', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getIngredientsSalesExcel = (
    params: IngredientsSalesExcelExportParams
  ): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/sales/ingredients-by-language/excel', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getDishesSalesPdf = (params: SalesPdfExportParams): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/sales/dishes-pdf', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  static getInventoryPdf = (params: InventoryPdfExportParams): Promise<Blob> =>
    HTTPInstanceFastApiWithPrefix.get('/sales/inventory-pdf', {
      params,
      responseType: 'blob'
    }).then(r => r.data)

  // ── AI / Chatbot ──

  static classifySentiment = (
    payload: OpinionAnalysisRequest
  ): Promise<OpinionAnalysisResult> =>
    HTTPInstanceFastApi.post('/api/classify-sentiment', payload).then(
      r => r.data
    )

  // ── Menu / Image ──

  static getColors = (image: File): Promise<string[]> => {
    const formData = new FormData()
    formData.append('image', image)
    return HTTPInstanceFastApi.post('/api/colors', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data)
  }

  static getMenuItems = (
    restaurantId: string,
    image: File
  ): Promise<MenuResponse> => {
    const formData = new FormData()
    formData.append('image', image)
    return HTTPInstanceFastApi.post(
      `/api/restaurants/${restaurantId}/get-menu-items`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ).then(r => r.data)
  }

  static getMenuItemsTest = (image: File): Promise<MenuResponse> => {
    const formData = new FormData()
    formData.append('image', image)
    return HTTPInstanceFastApi.post('/api/menu-items-test', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data)
  }

  // ── Restaurant ──

  static createTestRestaurant = (): Promise<unknown> =>
    HTTPInstanceFastApi.post('/api/create_restaurant').then(r => r.data)

  // Translation
  static translateToAllLanguages = (
    dataToTranslate: Record<string, unknown>[]
  ): Promise<TranslationItem[]> =>
    HTTPInstanceFastApi.post(
      '/api/translate-to-selected-language',
      dataToTranslate
    ).then(r => r.data)
}

export default InsightsAPI
